import { Button } from "@babackapp/customer-ui/components/button";
import { Input } from "@babackapp/customer-ui/components/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@babackapp/customer-ui/components/popover";
import { usePublicProductSearch } from "@babackapp/private-api-client/client/use-public-product-search";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { XIcon } from "lucide-react";
import { useState } from "react";
import { useDebouncedCallback } from "use-debounce";

export type CatalogProductItem = {
  id: string;
  title: string;
  imageUrl?: string | null;
};

type Props = {
  shopId: string;
  placeholder: string;
  selected: CatalogProductItem | null;
  onSelectedChange: (item: CatalogProductItem | null) => void;
};

export function CatalogProductCombobox({
  shopId,
  placeholder,
  selected,
  onSelectedChange,
}: Props) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [query, setQuery] = useState("");
  const debouncedSetQuery = useDebouncedCallback(setQuery, 300);
  const t = useCustomText();

  const { data: items = [], isFetching } = usePublicProductSearch(shopId, query);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setInputValue(e.target.value);
    debouncedSetQuery(e.target.value);
  }

  function handleSelect(item: CatalogProductItem) {
    onSelectedChange(item);
    setInputValue("");
    setQuery("");
    setOpen(false);
  }

  if (selected) {
    return (
      <div className="flex h-8 w-full items-center justify-between gap-2 rounded-lg border border-input bg-transparent px-2.5 py-1 text-sm">
        <div className="flex min-w-0 items-center gap-2">
          <div className="size-5 shrink-0 overflow-hidden rounded-sm bg-muted">
            {selected.imageUrl && (
              <img src={selected.imageUrl} alt="" className="size-full object-cover" />
            )}
          </div>
          <span className="truncate">{selected.title}</span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-6 shrink-0"
          onClick={() => onSelectedChange(null)}
          aria-label={t("catalogSearch.clear")}
        >
          <XIcon className="size-3.5" />
        </Button>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="h-8 w-full min-w-0 rounded-lg border border-input bg-transparent px-2.5 py-1 text-left text-base text-muted-foreground outline-none transition-colors cursor-pointer focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 md:text-sm"
        >
          {placeholder}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
        <div className="border-b border-border p-2">
          <Input
            placeholder={placeholder}
            value={inputValue}
            onChange={handleChange}
            autoFocus
          />
        </div>
        <div className="max-h-60 overflow-y-auto">
          {!query ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              {t("catalogSearch.startTyping")}
            </div>
          ) : isFetching ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              {t("catalogSearch.searching")}
            </div>
          ) : items.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              {t("catalogSearch.noResults")}
            </div>
          ) : (
            items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => handleSelect(item)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors cursor-pointer hover:bg-accent hover:text-accent-foreground"
              >
                <div className="size-6 shrink-0 overflow-hidden rounded-md bg-muted">
                  {item.imageUrl && (
                    <img src={item.imageUrl} alt="" className="size-full object-cover" />
                  )}
                </div>
                <span className="truncate">{item.title}</span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
